import { apiGet, apiPost } from '@/services/api/http'
import { AppError } from '@/services/api/errors'

export type CallOutcome =
  'interested' | 'follow_up' | 'no_answer' | 'busy' | 'not_interested' | 'wrong_number' | 'dropped'

export type CallStatus =
  | 'initiated'
  | 'ringing'
  | 'in_progress'
  | 'on_hold'
  | 'completed'
  | 'busy'
  | 'no_answer'
  | 'failed'
  | 'canceled'
  | 'dropped'

export interface CallLeadRef {
  id: string
  leadNo: string
  businessName: string
  name: string
  status: string
}

export interface CallSession {
  id: string
  callNo: string
  mode: 'manual' | 'webrtc'
  provider: string
  status: CallStatus
  outcome: CallOutcome | null
  agentUserId: string
  agentName: string | null
  subjectType: 'sales_lead'
  subjectId: string
  salesLeadId: string | null
  lead: CallLeadRef | null
  maskedNumber: string | null
  initiatedAt: string
  endedAt: string | null
  talkSec: number | null
  wrapUpAt: string | null
  pendingWrapUp: boolean
}

export interface CallSettings {
  callingStartMinute: number
  callingEndMinute: number
  callingStart: string
  callingEnd: string
  callingDays: number[]
  dailyCallLimit: number
  recentCallMinutes: number
}

export interface CallConfig {
  /** Server master switch (CALLS_ENABLED). */
  enabled: boolean
  canDial: boolean
  mode: 'manual' | 'webrtc'
  provider: string
  withinCallingHours: boolean
  settings: CallSettings
  pendingWrapUps: CallSession[]
}

export interface StartCallInput {
  subjectType: 'sales_lead'
  subjectId: string
  confirmRecent?: boolean
  client?: { device: 'phone' | 'desktop'; userAgent?: string }
}

export interface StartCallResult {
  call: CallSession
  /** Full number for the device that dials (tel: link / copy). */
  dial: { number: string }
  warnings: { code: string; message: string }[]
}

export interface WrapUpInput {
  outcome: CallOutcome
  note?: string
  callbackAt?: string | null
  /** Save & Next — also return the next lead to call. */
  next?: boolean
}

export interface NextLead {
  id: string
  leadNo: string
  businessName: string
  name: string
  status: string
  callbackAt: string | null
  reason: 'callback' | 'follow_up' | 'new'
}

export interface NextLeadResult {
  lead: NextLead | null
  reason: 'callback' | 'follow_up' | 'new' | 'none_left' | 'outside_hours'
}

export const callService = {
  config: () => apiGet<CallConfig>('/calls/config'),
  start: (input: StartCallInput) => apiPost<StartCallResult>('/calls', input),
  clientEvent: (id: string, type: 'tel_opened' | 'number_copied' | 'returned') =>
    apiPost<{ ok: boolean }>(`/calls/${id}/client-event`, { type }),
  cancel: (id: string) => apiPost<CallSession>(`/calls/${id}/cancel`),
  wrapUp: (id: string, input: WrapUpInput) =>
    apiPost<{ call: CallSession; next?: NextLeadResult }>(`/calls/${id}/wrap-up`, input),
  next: () => apiGet<NextLeadResult>('/calls/next'),
  get: (id: string) => apiGet<CallSession>(`/calls/${id}`),
}

/** API error body `{ code, errors }` (e.g. CALL_WRAP_UP_PENDING + { callId }). */
export function callErrorInfo(error: unknown): {
  code?: string
  errors?: Record<string, unknown>
} {
  const original = error instanceof AppError ? error.original : error
  const data = (original as { response?: { data?: { code?: unknown; errors?: unknown } } } | undefined)?.response?.data
  return {
    code: typeof data?.code === 'string' ? data.code : undefined,
    errors: data?.errors && typeof data.errors === 'object' ? (data.errors as Record<string, unknown>) : undefined,
  }
}
